import React, { useEffect } from 'react';
import Logo from './assets/logo-bolao-top-cropped.png';

const BolaoCopa2026 = ({ onBack, onLoginClick }) => {
  useEffect(() => {
    // SEO setup
    const title = "Como fazer bolão da Copa 2026 (guia completo + grátis) | Bolões TOP";
    const description = "Aprenda como fazer um bolão da Copa do Mundo 2026 passo a passo. Crie grátis, com ranking automático e sem limites.";
    const url = "https://boloestop.com.br/bolao-copa-2026";
    
    document.title = title;
    
    // Update Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    // Update Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Update OG Tags
    const updateMetaTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:url', url);
    updateMetaTag('og:type', 'article');

    // Add JSON-LD Structured Data
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": title,
      "description": description,
      "author": {
        "@type": "Organization",
        "name": "Bolões TOP"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Bolões TOP",
        "logo": {
          "@type": "ImageObject",
          "url": "https://boloestop.com.br/logo512.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": url
      }
    };

    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Como criar um bolão da Copa 2026 grátis?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Você pode criar um bolão grátis no Bolões TOP clicando no botão 'Criar Bolão Grátis', definindo as regras e compartilhando o link com seus amigos."
          }
        },
        {
          "@type": "Question",
          "name": "O Bolões TOP tem ranking automático?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sim, a plataforma calcula os pontos e atualiza o ranking em tempo real após o fim de cada partida."
          }
        }
      ]
    };

    const scriptSchema = document.createElement('script');
    scriptSchema.type = "application/ld+json";
    scriptSchema.id = "json-ld-article";
    scriptSchema.text = JSON.stringify(schemaData);
    document.head.appendChild(scriptSchema);

    const scriptFaq = document.createElement('script');
    scriptFaq.type = "application/ld+json";
    scriptFaq.id = "json-ld-faq";
    scriptFaq.text = JSON.stringify(faqData);
    document.head.appendChild(scriptFaq);

    window.scrollTo(0, 0);

    return () => {
      // Cleanup tags that are specific to this page
      const s1 = document.getElementById('json-ld-article');
      const s2 = document.getElementById('json-ld-faq');
      if (s1) s1.remove();
      if (s2) s2.remove();
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col min-h-screen text-slate-200 font-sans bg-[#0f172a]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-slate-700 shadow-lg bg-slate-900/70 backdrop-blur-md" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex-shrink-0 flex items-center cursor-pointer hover:opacity-80 transition-opacity" onClick={onBack}>
              <img src={Logo} alt="Bolões Top" className="h-14 w-auto" />
            </div>
            <div>
              <button
                onClick={onLoginClick}
                className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white font-bold py-1.5 px-4 sm:py-2 sm:px-6 text-xs sm:text-base rounded-full shadow-lg transform hover:scale-105 transition-all duration-200 whitespace-nowrap"
              >
                <span className="hidden sm:inline">CRIAR BOLÃO GRÁTIS</span>
                <span className="sm:hidden">CRIAR BOLÃO</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="relative py-16 md:py-24 overflow-hidden border-b border-slate-800">
           {/* Decorative Background */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 -right-24 w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-8">
              🏆 Como fazer um <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-400">bolão da Copa do Mundo 2026</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto italic">
              "Se você quer deixar os jogos ainda mais emocionantes, aprender como fazer um bolão da Copa do Mundo FIFA 2026 é uma das melhores ideias."
            </p>
          </div>
        </div>

        {/* Introduction Section */}
        <section className="py-16 bg-slate-900/50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-slate-800/80 p-8 rounded-3xl border border-slate-700 shadow-xl mb-12">
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                Seja entre amigos, família ou colegas de trabalho, o bolão transforma cada partida em uma disputa divertida — e até competitiva.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed font-semibold text-green-400">
                Neste guia, você vai aprender passo a passo como criar um bolão simples, organizado e sem dor de cabeça — inclusive usando uma plataforma gratuita que automatiza tudo.
              </p>
            </div>

            <div className="space-y-16">
              {/* O que é um bolão */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                  <span className="text-3xl">⚽</span> O que é um bolão da Copa do Mundo?
                </h2>
                <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700">
                  <p className="text-slate-300 mb-6">
                    Um bolão é uma competição onde os participantes fazem palpites sobre os jogos da Copa. Normalmente, cada pessoa tenta prever:
                  </p>
                  <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <li className="bg-slate-700/50 p-4 rounded-xl flex items-center gap-3 text-white font-medium">
                      <span className="text-green-400 font-bold">✓</span> Placar das partidas
                    </li>
                    <li className="bg-slate-700/50 p-4 rounded-xl flex items-center gap-3 text-white font-medium">
                      <span className="text-green-400 font-bold">✓</span> Vencedores
                    </li>
                    <li className="bg-slate-700/50 p-4 rounded-xl flex items-center gap-3 text-white font-medium">
                      <span className="text-green-400 font-bold">✓</span> Classificações
                    </li>
                  </ul>
                  <p className="text-slate-400 mt-6 text-sm italic">
                    Quem fizer mais pontos ao longo do campeonato, vence.
                  </p>
                </div>
              </div>

              {/* Passo a Passo */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 flex items-center gap-3">
                  <span className="text-3xl">📋</span> Como fazer um bolão da Copa 2026 (passo a passo)
                </h2>
                
                <div className="space-y-8">
                  {/* Step 1 */}
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-2xl font-black text-slate-900 shadow-[0_0_15px_rgba(34,197,94,0.4)]">1</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-white mb-3">Defina regras simples</h3>
                      <p className="text-slate-400 mb-4">Antes de começar, defina pontuação por acerto, bônus por fases finais e critérios de desempate.</p>
                      <div className="bg-green-500/10 border-l-4 border-green-500 p-4 rounded-r-xl mb-4">
                        <p className="text-green-400 font-bold">💡 Quanto mais simples, maior o engajamento.</p>
                      </div>
                      <button onClick={() => window.location.href='/regras-bolao-online-gratis'} className="text-teal-400 hover:underline text-sm font-bold">
                         ➡️ Veja nosso guia de regras completo
                      </button>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-2xl font-black text-slate-900 shadow-[0_0_15px_rgba(250,204,21,0.4)]">2</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-white mb-3">Escolha como organizar (aqui está o segredo)</h3>
                      <p className="text-slate-400 mb-6">Você pode fazer de 3 formas:</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-slate-800 p-5 rounded-2xl border border-slate-700 opacity-60">
                          <h4 className="text-red-400 font-bold mb-2 flex items-center gap-2">❌ Planilha</h4>
                          <p className="text-xs text-slate-500">Trabalho manual e erros frequentes.</p>
                        </div>
                        <div className="bg-slate-800 p-5 rounded-2xl border border-slate-700 opacity-60">
                          <h4 className="text-red-400 font-bold mb-2 flex items-center gap-2">❌ WhatsApp</h4>
                          <p className="text-xs text-slate-500">Controle manual que vira bagunça rápido.</p>
                        </div>
                        <div className="bg-green-500/10 p-5 rounded-2xl border-2 border-green-500 shadow-[0_0_15px_rgba(34,197,94,0.2)]">
                          <h4 className="text-green-400 font-bold mb-2 flex items-center gap-2">✅ Plataforma Online</h4>
                          <p className="text-xs text-green-300/70">Ranking automático e cálculo instantâneo. Melhor opção!</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Call to Action Card */}
              <div className="bg-gradient-to-br from-green-600 to-slate-900 p-8 md:p-12 rounded-[2rem] shadow-2xl border border-green-500/30 text-center animate-pulse-subtle">
                <h3 className="text-2xl md:text-4xl font-black text-white mb-6">
                  🚀 Crie seu bolão em <span className="text-yellow-400">2 minutos</span>
                </h3>
                <p className="text-lg text-slate-200 mb-10 max-w-xl mx-auto">
                  Sem complicação. O Bolões TOP automatiza tudo gratuitamente para você e seus amigos.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left mb-10 max-w-2xl mx-auto">
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> Ranking em tempo real
                  </div>
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> Notificações automáticas
                  </div>
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> Convite via WhatsApp
                  </div>
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> 100% Grátis e sem limites
                  </div>
                </div>
                <button
                  onClick={onLoginClick}
                  className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-black py-4 px-12 rounded-2xl text-xl shadow-xl transform hover:scale-105 transition-all"
                >
                  CRIAR BOLÃO GRÁTIS AGORA
                </button>
                <p className="text-green-200/60 mt-6 text-sm">Acesse: boloestop.com.br</p>
              </div>

              {/* Informative Sections */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                 <div className="bg-slate-800/40 p-8 rounded-3xl border border-slate-700/50">
                    <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                       <span className="text-2xl">🔥</span> Por que usar o Bolões TOP?
                    </h3>
                    <ul className="space-y-3 text-slate-300">
                       <li className="flex gap-2"><span className="text-green-500 font-bold">✔</span> 100% gratuito (sem anúncios)</li>
                       <li className="flex gap-2"><span className="text-green-500 font-bold">✔</span> Sem limite de participantes</li>
                       <li className="flex gap-2"><span className="text-green-500 font-bold">✔</span> Interface simples e rápida</li>
                       <li className="flex gap-2"><span className="text-green-500 font-bold">✔</span> Ranking automático</li>
                       <li className="flex gap-2"><span className="text-green-500 font-bold">✔</span> Bolão de Futebol e Fórmula 1</li>
                    </ul>
                 </div>
                 <div className="bg-slate-800/40 p-8 rounded-3xl border border-slate-700/50">
                    <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                       <span className="text-2xl">👥</span> Quem pode participar?
                    </h3>
                    <p className="text-slate-400 mb-4">Você pode criar bolões para:</p>
                    <div className="flex flex-wrap gap-2 text-xs font-bold font-mono">
                      <span className="bg-slate-700 px-3 py-1 rounded-full text-blue-400 border border-blue-400/30 uppercase">Amigos</span>
                      <span className="bg-slate-700 px-3 py-1 rounded-full text-green-400 border border-green-400/30 uppercase">Família</span>
                      <span className="bg-slate-700 px-3 py-1 rounded-full text-purple-400 border border-purple-400/30 uppercase">Trabalho (Empresas)</span>
                      <span className="bg-slate-700 px-3 py-1 rounded-full text-yellow-400 border border-yellow-400/30 uppercase">Grupos Grandes</span>
                    </div>
                    <p className="text-slate-500 mt-6 text-sm">Não existe limite de participantes no Bolões TOP.</p>
                 </div>
              </div>

              {/* Dicas e Finalização */}
              <div className="space-y-12">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                    <span className="text-3xl">💡</span> Dicas para um bolão de sucesso
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      "Defina prazos claros (até o apito inicial)",
                      "Incentive a participação constante",
                      "Use ranking automático para evitar brigas",
                      "Escolha uma plataforma confiável"
                    ].map((tip, i) => (
                      <div key={i} className="flex gap-3 bg-slate-800 p-4 rounded-xl border border-slate-700/50">
                        <span className="text-yellow-400">★</span>
                        <span className="text-slate-300 text-sm">{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700 text-center">
                  <h2 className="text-2xl font-bold text-white mb-4">🌍 Prepare-se para a Copa 2026</h2>
                  <p className="text-slate-400 leading-relaxed mb-6">
                    A Copa do Mundo FIFA 2026 será uma das maiores da história — com mais jogos e mais oportunidades de palpites. 
                    <strong className="text-green-400"> Quanto antes você organizar seu bolão, melhor.</strong>
                  </p>
                  <div className="h-px bg-slate-700 w-full mb-8"></div>
                  <h3 className="text-xl font-bold text-white mb-4">🎯 Conclusão</h3>
                  <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
                    Criar um bolão da Copa do Mundo 2026 pode ser simples — e muito mais divertido do que assistir sozinho.
                    Se você quer praticidade, organização e zero dor de cabeça use o <span className="text-teal-400 font-bold">Bolões TOP</span>.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                      onClick={onLoginClick}
                      className="bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-8 rounded-xl shadow-lg transition-all"
                    >
                      COMEÇAR AGORA
                    </button>
                    <button
                      onClick={() => window.location.href='/criar-organizar-bolao-gratis'}
                      className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 px-8 rounded-xl shadow-lg transition-all"
                    >
                      COMO ORGANIZAR
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 pt-12 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center italic text-slate-500 text-sm">
          <p>&copy; 2026 Bolões Top. O guia definitivo para seu bolão.</p>
          <button onClick={scrollToTop} className="mt-4 text-teal-500 hover:underline">Voltar ao topo</button>
        </div>
      </footer>

      {/* Styles for Animations */}
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.01); }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 4s infinite ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default BolaoCopa2026;
